/**
 * Modal de gestion de roles — Sprint 12 S12-05.
 *
 * Permite asignar y revocar roles de una persona. Consume los
 * endpoints de S11-V2-12:
 * - POST /personas/{id}/roles → asignar.
 * - DELETE /personas/{id}/roles/{rol} → revocar.
 *
 * Prerequisitos doctrinarios (convencion #14): el backend rechaza con
 * 422 si se viola (ej. tesorero sin comision vigente).
 * El modal muestra el `detail` retornado y permite reintento manual.
 *
 * UI:
 * - Lista de roles vigentes con boton "Revocar" por cada uno.
 * - Selector + boton "Asignar" para sumar un rol nuevo.
 * - Excluye los roles ya vigentes del selector (no se puede asignar
 *   dos veces el mismo).
 * - super_admin no aparece en el selector (no asignable por API,
 *   convencion S11 / ROLES_ASIGNABLES).
 */
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { formatRut } from '../../lib/format';
import {
  asignarRolPersona,
  revocarRolPersona,
} from '../../lib/api/persona_roles';
import type {
  PersonaConRoles,
  RolAsignable,
} from '../../lib/api/types';
import { ETIQUETA_ROL, ROLES_ASIGNABLES } from '../../lib/api/types';
import { Button } from '../../components/ui/Button';

interface ModalGestionRolesProps {
  persona: PersonaConRoles;
  onCerrar: () => void;
  onActualizado: () => void;
}

export function ModalGestionRoles({
  persona,
  onCerrar,
  onActualizado,
}: ModalGestionRolesProps) {
  const rolesAsignables = ROLES_ASIGNABLES.filter(
    (r) => !persona.roles_vigentes.includes(r),
  );
  const [rolNuevo, setRolNuevo] = useState<RolAsignable | ''>(
    rolesAsignables[0] ?? '',
  );
  // La persona aún no tiene login si no hay usuario_id. En ese caso, asignar
  // un rol provisiona la cuenta (Auth + Usuario) y se requiere el email.
  const sinLogin = persona.usuario_id === null;
  const [email, setEmail] = useState(persona.email ?? '');
  const [motivoRevocacion, setMotivoRevocacion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mensajeExito, setMensajeExito] = useState<string | null>(null);

  function manejarError(err: unknown): string {
    if (err instanceof ApiError) {
      const detail =
        typeof err.body === 'object' && err.body && 'detail' in err.body
          ? String((err.body as { detail: unknown }).detail)
          : `Error ${err.status}`;
      return detail;
    }
    return 'Error de red';
  }

  async function handleAsignar(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!rolNuevo) {
      setError('Selecciona un rol para asignar.');
      return;
    }
    if (sinLogin && !email.trim()) {
      setError(
        'La persona aún no tiene acceso al portal. Indica un email para crear su cuenta.',
      );
      return;
    }
    setEnviando(true);
    setError(null);
    setMensajeExito(null);
    try {
      await asignarRolPersona(persona.id, {
        rol: rolNuevo,
        email: sinLogin ? email.trim() : undefined,
      });
      setMensajeExito(
        `Rol "${ETIQUETA_ROL[rolNuevo] ?? rolNuevo}" asignado.`,
      );
      // Notificar para que la pagina padre refresque y cierre.
      onActualizado();
    } catch (err) {
      setError(manejarError(err));
    } finally {
      setEnviando(false);
    }
  }

  async function handleRevocar(rol: string) {
    if (
      !window.confirm(
        `¿Revocar el rol "${ETIQUETA_ROL[rol] ?? rol}" de ${persona.nombre_completo}?`,
      )
    ) {
      return;
    }
    setEnviando(true);
    setError(null);
    setMensajeExito(null);
    try {
      await revocarRolPersona(persona.id, rol, {
        motivo_revocacion: motivoRevocacion.trim() || null,
      });
      setMensajeExito(`Rol "${ETIQUETA_ROL[rol] ?? rol}" revocado.`);
      onActualizado();
    } catch (err) {
      setError(manejarError(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onCerrar}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-roles-titulo"
    >
      <div
        className="bg-bg-elevated border-2 border-admin-red rounded-md p-6 max-w-2xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2
              id="modal-roles-titulo"
              className="text-xl font-medium text-text-primary"
            >
              Gestionar roles
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              {persona.nombre_completo} · {formatRut(persona.rut)}
              {persona.email && ` · ${persona.email}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="text-text-secondary hover:text-text-primary text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="space-y-5">
          {/* Roles vigentes */}
          <div>
            <h3 className="text-sm font-medium text-text-primary mb-2">
              Roles vigentes
            </h3>
            {persona.roles_vigentes.length === 0 ? (
              <p className="text-sm text-text-muted">Sin roles asignados.</p>
            ) : (
              <ul className="space-y-2">
                {persona.roles_vigentes.map((rol) => (
                  <li
                    key={rol}
                    className="flex items-center justify-between bg-bg-sunken border-[0.5px] border-white/[0.08] rounded-sm px-3 py-2"
                    data-testid={`rol-vigente-${rol}`}
                  >
                    <span className="text-sm text-text-primary">
                      {ETIQUETA_ROL[rol] ?? rol}
                    </span>
                    {rol === 'super_admin' ? (
                      <span className="text-xs text-text-muted">
                        No revocable por API
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleRevocar(rol)}
                        disabled={enviando}
                        className="text-sm text-state-danger hover:underline disabled:opacity-50"
                      >
                        Revocar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-2">
              <label
                htmlFor="motivo-revocacion"
                className="block text-xs text-text-secondary mb-1"
              >
                Motivo de revocación (opcional, se aplica a la próxima revocación)
              </label>
              <input
                id="motivo-revocacion"
                type="text"
                value={motivoRevocacion}
                onChange={(e) => setMotivoRevocacion(e.target.value)}
                maxLength={500}
                placeholder="Ej. Sale de la directiva 2026-06"
                className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary text-sm px-2 py-1 placeholder:text-text-muted"
              />
            </div>
          </div>

          {/* Asignar nuevo */}
          <div className="border-t-[0.5px] border-white/[0.08] pt-4">
            <h3 className="text-sm font-medium text-text-primary mb-2">
              Asignar nuevo rol
            </h3>
            {rolesAsignables.length === 0 ? (
              <p className="text-sm text-text-muted">
                La persona ya tiene todos los roles asignables.
              </p>
            ) : (
              <form onSubmit={handleAsignar} className="space-y-2">
                {sinLogin && (
                  <div>
                    <label
                      htmlFor="email-provision"
                      className="block text-xs text-text-secondary mb-1"
                    >
                      Email para el acceso al portal{' '}
                      <span className="text-state-danger">*</span>
                      <span className="text-text-muted">
                        {' '}— la persona aún no tiene cuenta; al asignar el rol
                        se crea su login.
                      </span>
                    </label>
                    <input
                      id="email-provision"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vecino@example.org"
                      className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary text-sm px-2 py-1 placeholder:text-text-muted"
                    />
                  </div>
                )}
                <div className="flex gap-2 items-center">
                  <select
                    aria-label="Rol a asignar"
                    value={rolNuevo}
                    onChange={(e) => setRolNuevo(e.target.value as RolAsignable)}
                    className="flex-1 rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 text-sm"
                  >
                    {rolesAsignables.map((r) => (
                      <option key={r} value={r}>
                        {ETIQUETA_ROL[r] ?? r}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    disabled={enviando || !rolNuevo}
                  >
                    {enviando ? 'Asignando…' : 'Asignar rol'}
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* Mensajes */}
          {error && (
            <div
              className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
              role="alert"
            >
              {error}
            </div>
          )}
          {mensajeExito && !error && (
            <div className="bg-state-success/15 border-l-4 border-state-success p-3 rounded-sm text-sm text-text-primary">
              {mensajeExito}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 mt-4 border-t-[0.5px] border-white/[0.08]">
          <Button type="button" variant="ghost" onClick={onCerrar}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}
